import * as Material from "@mui/material";
import { useTranslation } from "react-i18next";

import "./styles.css";

import TitleBreadcrumb from '@components/TitleBreadcrumb';

import { useCommunity } from "contexts/community";
import Metric from "./components/Metric";
import Chart from "./components/Chart";
import { useEffect, useState } from "react";

import api from "@helpers/api.js";
import Select from "./components/Select";
import { InsightsRounded } from "@mui/icons-material";


export default function Communities() {
  const { t } = useTranslation("dashboard");

  document.title = t("dashboardPage.documentTitle");

  const [loading, setLoading] = useState(true);

  const { communities } = useCommunity();

  const [communitiesAvailable, setCommunitiesAvailable] = useState([]);

  const [community, setCommunity] = useState(
    communitiesAvailable[0]?.id || null
  );

  const [data, setData] = useState({
    subscribers: {
      total: 0,
      period_comparision: 0,

      history: {
        values: [
          {
            label: t("months.short.jan"),
            value: 0,
          },
          {
            label: t("months.short.feb"),
            value: 0,
          },
          {
            label: t("months.short.mar"),
            value: 0,
          },
          {
            label: t("months.short.apr"),
            value: 0,
          },
          {
            label: t("months.short.may"),
            value: 0,
          },
          {
            label: t("months.short.jun"),
            value: 0,
          },
        ],
      },
    },

    receipt: {
      total: 0,
      period_comparision: 0,

      history: {
        granularity: "monthly",
        period: 6,
        mode: "liquid",
        values: [
          {
            label: t("months.short.jan"),
            value: 0,
          },
          {
            label: t("months.short.feb"),
            value: 0,
          },
          {
            label: t("months.short.mar"),
            value: 0,
          },
          {
            label: t("months.short.apr"),
            value: 0,
          },
          {
            label: t("months.short.may"),
            value: 0,
          },
          {
            label: t("months.short.jun"),
            value: 0,
          },
        ],
      },
    },

    funnel: [
      { id: "page_visitor", value: 0, label: t("dashboardPage.funnel.visitors") },
      { id: "plan_chosen", value: 0, label: t("dashboardPage.funnel.planChosen") },
      { id: "lead", value: 0, label: t("dashboardPage.funnel.leads") },
      { id: "subscribe", value: 0, label: t("dashboardPage.funnel.subscribers"), active: true },
    ],

    mrr: 0,
    acl: 0,
    ltv: 0,
    chargeback: 0,
    churn: 0,
  });

  const [interval, setInterval] = useState("last_week");

  window.communities = {};

  useEffect(() => {
    const available = communities?.filter((c) => c.role === "creator") || [];

    setCommunitiesAvailable([...available]);
    setCommunity(available[0]?.id);
  }, [communities]);

  useEffect(() => {
    handleLoad();
  }, [community, interval]);

  async function handleLoad() {
    setLoading(true);

    try {
      const params = `interval=${interval}&community=${community}&granularity=daily`;
      const { data } = await api.get("insights/full?" + params);

      if (data) {
        data.funnel = data.funnel.map((f) => {
          if (f.id === "subscribe") f.active = true;
          return f;
        });
        setData({ ...data });
      }
    } catch (e) {
      console.log(e);
    }

    setLoading(false);
  }

  return (
    <div className="screen-body">
      <TitleBreadcrumb title={[
        {
          "title": t("dashboardPage.breadcrumbReports"),
          "url": "dashboard",
          iconLeft: <InsightsRounded color="white" />
        },
        {
          "title": t("dashboardPage.breadcrumbGeneral")
        }
      ]} />
      <div>
          <div className="filters-top mt-1 mb-3">
            <div className="community-select-row mt-2 mb-1">
              <h4>{t("dashboardPage.filterByInterval")}</h4>
              <Metric.OptionsSelect
                onChange={(v) => setInterval(v.target.value)}
                value={interval}
                options={[
                  {
                    label: t("dashboardPage.intervals.lastWeek"),
                    value: "last_week",
                  },
                  {
                    label: t("dashboardPage.intervals.thisMonth"),
                    value: "this_month",
                  },
                  {
                    label: t("dashboardPage.intervals.lastMonth"),
                    value: "last_month",
                  },
                  {
                    label: t("dashboardPage.intervals.thisQuarter"),
                    value: "this_quarter",
                  },
                  {
                    label: t("dashboardPage.intervals.thisYear"),
                    value: "this_year",
                  },
                ]}
              />
            </div>

            <div className="community-select-row mt-2 mb-1">
              <h4>{t("dashboardPage.communityLabel")}</h4>
              <Select
                options={communitiesAvailable.map((c) => ({
                  id: c.id,
                  label: c.name,
                }))}
                value={community}
                key={community}
                onChange={setCommunity}
              />
            </div>
          </div>

          <Metric.Row>
            <div
              style={{
                flex: 1,
                maxWidth: "380px",
              }}
            >
              <ActiveUsersAmount {...{ data, loading }} />
              <UsersGrowthHistory {...{ data, loading }} />
            </div>
            <Incomings
              key={loading}
              {...{ data, loading, community, interval }}
            />
          </Metric.Row>

          <Metric.Row>
            <Funnel {...{ data, loading }} />
          </Metric.Row>

          <Metric.Row>
            <MRR {...{ data, loading }} />
            <ACL {...{ data, loading }} />
            <LTV {...{ data, loading }} />
            <ChargeBack {...{ data, loading }} />
            <Churn {...{ data, loading }} />
          </Metric.Row>
      </div>
    </div>
  );
}

function ActiveUsersAmount({ data, loading }) {
  const { t } = useTranslation("dashboard");

  return (
    <Metric.Box
      className="mb-2"
      loading={loading}
      title={t("dashboardPage.activeSubscribers")}
      type="main"
    >
      <Metric.Value
        size="large"
        type="number"
        value={data?.subscribers.total}
      />
      <Metric.Comparision
        value={data?.subscribers.period_comparision}
        type="white"
      />
    </Metric.Box>
  );
}

function Incomings({ data, loading, community, interval }) {
  const { t } = useTranslation("dashboard");

  const [mode, setMode] = useState("full");

  const [localLoading, setLocalLoading] = useState(loading);
  const [localData, setLocalData] = useState(data?.receipt);

  const [granularity, setGranularity] = useState(
    localData?.history.granularity || "monthly"
  );

  const revenue = localData?.history.values.map((v) => v[mode]);
  const labels = localData?.history.values.map((v) => v.label);

  async function handleLoad(info) {
    setLocalLoading(true);

    try {
      const params = `interval=${interval}&community=${community}&granularity=${info?.granularity || granularity
        }`;
      const { data } = await api.get("insights/receipts?" + params);

      if (data) {
        setLocalData({ ...data });
      }
    } catch (e) {
      console.log(e);
    }

    setLocalLoading(false);
  }

  function handleModeChange(v) {
    setMode(v);
  }
  function handleGranularityChange(v) {
    setGranularity(v);
    handleLoad({ granularity: v });
  }

  return (
    <Metric.Box
      loading={localLoading}
      minWidth={300}
      title={t("dashboardPage.revenue.title")}
      titleRight={
        <Metric.Comparision
          value={localData?.period_comparision}
          mode="percent"
        />
      }
    >
      <Metric.Row>
        <Metric.Value
          size="large"
          type="money"
          value={data.receipt.total[mode]}
        />
        <Metric.OptionsSelect
          onChange={(v) => handleModeChange(v.target.value)}
          value={mode}
          options={[
            {
              label: t("dashboardPage.revenue.gross"),
              value: "full",
            },
            {
              label: t("dashboardPage.revenue.net"),
              value: "liquid",
            },
          ]}
          className="mb-2"
        />
      </Metric.Row>
      <div>
        <Metric.Row noWrap={true} center>
          <h4>{t("dashboardPage.revenue.granularity")}</h4>

          <Select
            options={[
              {
                label: t("dashboardPage.revenue.granularities.daily"),
                id: "daily",
              },
              {
                label: t("dashboardPage.revenue.granularities.weekly"),
                id: "weekly",
              },
              {
                label: t("dashboardPage.revenue.granularities.monthly"),
                id: "monthly",
              },
              {
                label: t("dashboardPage.revenue.granularities.quarterly"),
                id: "quarterly",
              },
              {
                label: t("dashboardPage.revenue.granularities.biannually"),
                id: "biannually",
              },
              {
                label: t("dashboardPage.revenue.granularities.yearly"),
                id: "yearly",
              },
            ]}
            value={granularity}
            onChange={handleGranularityChange}
          />
        </Metric.Row>
        <Chart.Line
          key={Math.random()}
          type="money"
          data={revenue}
          labels={labels}
        />
      </div>
    </Metric.Box>
  );
}

function UsersGrowthHistory({ data, loading }) {
  const { t } = useTranslation("dashboard");

  const revenue = data?.subscribers.history.values.map((v) => v.value);
  const labels = data?.subscribers.history.values.map((v) => v.label);

  return (
    <Metric.Box loading={loading} title={t("dashboardPage.subscribersHistory")}>
      <Chart.Line height={200} data={revenue} labels={labels} />
    </Metric.Box>
  );
}
function Funnel({ data, loading }) {
  const { t } = useTranslation("dashboard");

  return (
    <Metric.Box loading={loading} centered title={t("dashboardPage.funnel.title")}>
      <Chart.Funnel data={data?.funnel} />
    </Metric.Box>
  );
}
function MRR({ data, loading }) {
  const { t } = useTranslation("dashboard");

  return (
    <Metric.Box
      loading={loading}
      centered
      minWidth={180}
      maxWidth={250}
      title={t("dashboardPage.mrr.title")}
    >
      <Metric.Value type="money" value={data?.mrr.liquid} />
      <Metric.Info text={t("dashboardPage.mrr.info")} />
    </Metric.Box>
  );
}
function ACL({ data, loading }) {
  const { t } = useTranslation("dashboard");

  return (
    <Metric.Box
      loading={loading}
      centered
      minWidth={180}
      maxWidth={250}
      title={t("dashboardPage.acl.title")}
    >
      <Metric.Value type="text" value={t("dashboardPage.acl.value", { value: data?.acl })} />
      <Metric.Info text={t("dashboardPage.acl.info")} />
    </Metric.Box>
  );
}
function LTV({ data, loading }) {
  const { t } = useTranslation("dashboard");

  return (
    <Metric.Box
      loading={loading}
      centered
      minWidth={180}
      maxWidth={250}
      title={t("dashboardPage.ltv.title")}
    >
      <Metric.Value type="money" value={data?.ltv.liquid} />
      <Metric.Info text={t("dashboardPage.ltv.info")} />
    </Metric.Box>
  );
}
function ChargeBack({ data, loading }) {
  const { t } = useTranslation("dashboard");

  return (
    <Metric.Box
      loading={loading}
      centered
      minWidth={180}
      maxWidth={250}
      title={t("dashboardPage.chargebackRate.title")}
    >
      <Metric.Value type="percent" value={data?.chargeback} />
      <Metric.Info text={t("dashboardPage.chargebackRate.info")} />
    </Metric.Box>
  );
}
function Churn({ data, loading }) {
  const { t } = useTranslation("dashboard");

  return (
    <Metric.Box
      loading={loading}
      centered
      minWidth={180}
      maxWidth={250}
      title={t("dashboardPage.churn.title")}
    >
      <Metric.Value type="percent" value={data?.churn} />
      <Metric.Info text={t("dashboardPage.churn.info")} />
    </Metric.Box>
  );
}
